import type { ICallbackController } from '../interface/callback';

interface ICallbackRecord<Callback> {
    name: string;
    callback: Callback;
}

function callbackRegistry<CallbackMap>(): ICallbackController<CallbackMap> {
    const state: Partial<{
        [Event in keyof CallbackMap]: ICallbackRecord<CallbackMap[Event]>[];
    }> = {};

    const controller: ICallbackController<CallbackMap> = {
        subscribe: <Event extends keyof CallbackMap, Callback extends CallbackMap[Event]>(
            callback: Callback,
            event: Event,
            key?: string,
        ) => {
            const existingRecords = state[event] ?? [];
            state[event] = [
                ...existingRecords,
                { name: key ?? `callback-${existingRecords.length}`, callback },
            ];
        },
        unsubscribe: <Event extends keyof CallbackMap, Callback extends CallbackMap[Event]>(
            callback: Callback,
            event: Event,
        ) => {
            const existingRecords = (state[event] ?? []).filter(cb => cb.callback !== callback);
            state[event] = existingRecords;
        },
        search: <Event extends keyof CallbackMap, Callback extends CallbackMap[Event]>(
            subscriberKey: string,
            event: Event,
        ) =>
            (state[event] ?? [])
                .filter(record => record.name === subscriberKey)
                .map(record => record.callback) as Callback[],
        eventListeners: <Event extends keyof CallbackMap, Callback extends CallbackMap[Event]>(
            event: Event,
        ) => (state[event] ?? []).map(record => record.callback) as Callback[],
    };

    return controller;
}

export default Object.freeze({
    build: () => callbackRegistry(),
});
